import type { PropsWithChildren, ReactNode } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import { wacToken } from "@/libraries/design/foundations";
import { Asterisks } from "@/libraries/wpl/foundations/src/icons/software/asterisks";

const containerStyles = css({
    display: "flex",
    justifyContent: "flex-end",
    maxWidth: 1200,
    gap: 32,
    [media.below.md]: {
        width: "100%",
        flexDirection: "column-reverse",
        backgroundColor: wacToken("color.neutral.light0"),
        gap: 0
    },
    [media.only.md]: {
        height: "80%"
    },
    [media.only.sm]: {
        height: "80%"
    },
    [media.only.xs]: {
        height: "80%"
    },
    [media.below.xs]: {
        height: "90%"
    }
});

const containerReverseStyles = css({
    flexDirection: "row-reverse",

    [media.below.md]: {
        flexDirection: "column"
    }
});

const contentStyles = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginInlineStart: "auto",
    marginInlineEnd: "auto"
});

const textContentStyles = css({
    flexBasis: "30%",
    borderRadius: 22,
    background: wacToken("color.neutral.light0"),
    [media.below.md]: {
        width: "100%"
    },
    [media.above.sm]: {
        minWidth: 367
    }
});

const textSpaceStyles = css({
    paddingBlockStart: 12
});

const asterisksStyle = css({
    [media.below.md]: {
        display: "none"
    }
});

const eyebrowStyles = css({
    marginBlockEnd: 5
});

const titleStyles = css({
    marginBlock: 0
});

const descriptionStyles = css({
    fontSize: "1.25rem",
    marginBlockStart: 16,
    [media.below.sm]: {
        marginBlockEnd: 16
    }
});

const separatorStyles = css({
    height: "1px",
    background: wacToken("color.neutral.light500"),
    marginBlockStart: 32
});

const iconTitleStyles = css({
    marginBlockStart: 32,
    marginBlockEnd: 9,
    width: 241
});

const iconContainerStyles = css({
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "flex-start",
    listStyle: "none",
    paddingInline: 0,
    marginBlock: 0,
    gap: 8,
    [media.below.sm]: {
        gap: 4
    }
});

const iconAndTextStyles = css({
    "display": "flex",
    "justifyContent": "center",
    "alignItems": "flex-start",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".body-sm": {
        marginBlockStart: 0,
        marginBlockEnd: 0
    }
});

const iconStyles = css({
    width: 24,
    height: 24,
    textAlign: "center",
    flexShrink: 0
});

const buttonSpaceStyles = css({
    marginBlockStart: 32,
    [media.below.sm]: {
        marginBlockStart: 16,
        marginBlockEnd: 16
    }
});

const displayNoneBelowLg = css({
    [media.below.md]: {
        display: "none"
    }
});

export type FeatureInfoProps = PropsWithChildren<{
    cta?: ReactNode;
    eyebrow?: string;
    title: string;
    description: string;
    reverseLayout?: boolean;
    iconTitle?: string;
    iconCollection: { icon: ReactNode; description: string }[];
    showAsterisks?: boolean;
    hideSeparator?: boolean;
}>;

export const FeatureInfo = ({
    eyebrow,
    title,
    description,
    iconTitle,
    iconCollection,
    reverseLayout,
    cta,
    showAsterisks,
    hideSeparator,
    children
}: FeatureInfoProps) => {
    return (
        <div css={[containerStyles, reverseLayout && containerReverseStyles]}>
            <div css={contentStyles}>{children}</div>
            <div css={textContentStyles}>
                <div css={textSpaceStyles}>
                    {showAsterisks && (
                        <div css={asterisksStyle}>
                            <Asterisks size="64" />
                        </div>
                    )}
                    {eyebrow && (
                        <div css={eyebrowStyles} className="eyebrow-md">
                            {eyebrow}
                        </div>
                    )}
                    <h2 className="h3" css={titleStyles}>
                        {title}
                    </h2>
                    <p css={descriptionStyles}>{description}</p>
                    {!hideSeparator && (
                        <div css={[separatorStyles, displayNoneBelowLg]} />
                    )}
                    {iconTitle && (
                        <h3
                            className="body-md"
                            css={[iconTitleStyles, displayNoneBelowLg]}
                        >
                            {iconTitle}
                        </h3>
                    )}
                    <ul css={iconContainerStyles}>
                        {iconCollection.map((icon, index) => (
                            <li css={iconAndTextStyles} key={index}>
                                <div css={iconStyles}>{icon.icon}</div>
                                <p className="body-sm">{icon.description}</p>
                            </li>
                        ))}
                    </ul>
                    {cta && <div css={buttonSpaceStyles}>{cta}</div>}
                </div>
            </div>
        </div>
    );
};
